import { useEffect, useMemo } from 'react';
import { useRouter } from 'expo-router';
import {
  getOnboardingRouteForStep,
  isOnboardingComplete,
} from '@perdiem/trpc/onboarding';
import { ErrorState, LoadingSpinner, TrialActivationGate } from '../../components';
import { trpc } from '../../lib/trpc';

const IS_LOCAL_DEV_BYPASS = process.env['EXPO_PUBLIC_DEV_BYPASS'] === 'true';

export default function TrialActivationRoute() {
  const router = useRouter();
  // Always verify both values on entry. This route is commonly reached right
  // after advancing onboarding or completing checkout, when the shared query
  // cache may still contain the previous step or entitlement state.
  const householdQ = trpc.households.get.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const subscriptionQ = trpc.subscription.getStatus.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const isVerifyingAccess =
    householdQ.isLoading
    || householdQ.isFetching
    || subscriptionQ.isLoading
    || subscriptionQ.isFetching;
  const resumeRoute = useMemo(
    () => getOnboardingRouteForStep(householdQ.data?.onboardingStep),
    [householdQ.data?.onboardingStep],
  );

  const shouldResume = Boolean(
    !isVerifyingAccess
    && !householdQ.error
    && !subscriptionQ.error
    && householdQ.data
    && (
      IS_LOCAL_DEV_BYPASS
      || householdQ.data.isDemo
      || subscriptionQ.data?.isActive
      || (householdQ.data.onboardingStep ?? 0) <= 1
    ),
  );

  useEffect(() => {
    if (!householdQ.data || !shouldResume) return;
    router.replace(
      isOnboardingComplete(householdQ.data.onboardingStep)
        ? '/(app)'
        : resumeRoute as any,
    );
  }, [householdQ.data, resumeRoute, router, shouldResume]);

  if (isVerifyingAccess || shouldResume) {
    return <LoadingSpinner message="Checking your trial access..." />;
  }

  if (householdQ.error || subscriptionQ.error) {
    return (
      <ErrorState
        message="Could not load your trial access."
        error={householdQ.error ?? subscriptionQ.error}
        screen="trial-activation"
        metadata={{
          failedQueries: householdQ.error ? 'households.get' : 'subscription.getStatus',
        }}
        onRetry={() => {
          void Promise.all([householdQ.refetch(), subscriptionQ.refetch()]);
        }}
      />
    );
  }

  if (!householdQ.data) {
    return (
      <ErrorState
        message="We couldn't find your onboarding progress."
        screen="trial-activation"
        metadata={{ failedQueries: 'households.get', householdMissing: true }}
        onRetry={() => {
          void householdQ.refetch();
        }}
      />
    );
  }

  return (
    <TrialActivationGate
      onActivated={() => {
        void subscriptionQ.refetch();
      }}
    />
  );
}
